import {useSyncExternalStore} from 'react'

export type ThemeMode = 'light' | 'dark'

const THEME_STORAGE_KEY = 'tabler-theme'
const themeListeners = new Set<() => void>()

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark'
}

export function getThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark'

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
  return isThemeMode(storedTheme) ? storedTheme : 'dark'
}

export function applyTheme(theme: ThemeMode): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.classList.toggle('dark', theme === 'dark')
  root.setAttribute('data-theme', theme)
  root.style.colorScheme = theme
}

export function setThemeMode(theme: ThemeMode): void {
  applyTheme(theme)
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  }
  notifyThemeListeners()
}

export function initializeTheme(): ThemeMode {
  const theme = getThemeMode()
  applyTheme(theme)
  return theme
}

function notifyThemeListeners(): void {
  for (const listener of themeListeners) listener()
}

function subscribeToTheme(listener: () => void): () => void {
  themeListeners.add(listener)

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== THEME_STORAGE_KEY) return
    applyTheme(getThemeMode())
    notifyThemeListeners()
  }

  window.addEventListener('storage', handleStorage)

  return () => {
    themeListeners.delete(listener)
    window.removeEventListener('storage', handleStorage)
  }
}

export function useThemeMode(): [ThemeMode, (theme: ThemeMode) => void] {
  const theme = useSyncExternalStore(subscribeToTheme, getThemeMode, (): ThemeMode => 'dark')
  return [theme, setThemeMode]
}

// Compatibility facade for current callers; new code should use the functions above.
export class themeMode {
  static switchMode(): void {
    setThemeMode(getThemeMode() === 'dark' ? 'light' : 'dark')
  }
}
